import React, { Dispatch, ReactNode, RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";
import ImageUploading, { ImageListType, ImageType } from "react-images-uploading";
import { ExportInterface } from "react-images-uploading/dist/typings";
import { useDispatch, useSelector } from "react-redux";
import { MessageRepeatedCountData } from "@sms/shared/message/message-repeated-count.interface";
import { UserData } from "@sms/shared/users/users.interface";

import styles from "./right-column-footer.module.scss";
import { MessageState } from "./right-column-footer.types";
import { AutocompleteSuggestion } from "./autocomplete-suggestions.component";

import AttachIcon from "~/view/assets/attach-icon";
import DeleteIcon from "~/view/assets/delete-icon";
import { ApplicationStore } from "~/app/application.store";
import { createMessageAction } from "~/app/messages/messages.actions";

const RightColumnFooterComponent: React.FC = () => {
  const dispatch: Dispatch<any> = useDispatch();

  const authenticated: UserData | null = useSelector((state: ApplicationStore) => state.auth.user);
  const selectChatId: string | null = useSelector((state: ApplicationStore) => state.chats.selectChatId);
  const messagesRepeatedData: MessageRepeatedCountData[] = useSelector(
    (state: ApplicationStore) => state.message.messagesRepeated
  );

  const [message, setMessage]: [MessageState, React.Dispatch<React.SetStateAction<MessageState>>] =
    useState<MessageState>({
      message: ""
    });

  const [focusInput, setFocusInput]: [boolean, React.Dispatch<React.SetStateAction<boolean>>] =
    useState<boolean>(false);

  const [images, setImages]: [ImageType[], React.Dispatch<React.SetStateAction<ImageType[]>>] = useState<ImageType[]>(
    []
  );

  const maxNumber: number = 4;

  const inputSuggestionRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
  const textareaRef: RefObject<HTMLTextAreaElement | null> = useRef<HTMLTextAreaElement>(null);

  const onChange: (imageList: ImageListType) => void = (imageList: ImageListType): void => {
    setImages(imageList);
  };

  const handleChange: (target: { name: string; value: string }) => void = (target: {
    name: string;
    value: string;
  }): void => {
    setMessage({ ...message, [target.name]: target.value });
  };

  const handleSelectMessageRepeated: (value: string) => void = (value: string): void => {
    setMessage({ message: value });
    textareaRef?.current?.focus();
  };

  const handleCreateMessage: () => void = useCallback(() => {
    if ((message.message !== "" || images.length > 0) && authenticated && selectChatId) {
      dispatch(
        createMessageAction({
          message: message.message.replace(/\n+$/g, ""),
          images: images,
          user: { id: authenticated.id },
          chat: { id: selectChatId }
        })
      );

      setMessage({ message: "" });
      setImages([]);
    }
  }, [dispatch, message, images, authenticated, selectChatId]);

  const messagesRepeated: string[] = useMemo(
    () =>
      messagesRepeatedData
        .filter(messageRepeated =>
          message.message != ""
            ? messageRepeated.messageRepeated.toLowerCase().startsWith(message.message.toLowerCase()) &&
              messageRepeated.messageRepeated !== message.message
            : true
        )
        .slice(0, 5)
        .map(messageRepeated => messageRepeated.messageRepeated),
    [messagesRepeatedData, message.message]
  );

  useEffect(() => {
    const handleDocumentClick: (event: MouseEvent) => void = (event: MouseEvent) => {
      if (
        inputSuggestionRef.current &&
        !inputSuggestionRef.current.contains(event.target as HTMLFormElement) &&
        document.activeElement !== textareaRef.current
      ) {
        setFocusInput(false);
      }
    };

    document.addEventListener("click", handleDocumentClick);

    return (): void => document.removeEventListener("click", handleDocumentClick);
  }, []);

  return (
    <div className={styles.rightColumnFooter}>
      <ImageUploading multiple value={images} onChange={onChange} maxNumber={maxNumber}>
        {({ imageList, onImageUpload, onImageUpdate, onImageRemove, dragProps }: ExportInterface): ReactNode => (
          <>
            <div className={styles.newMessageWrapper} {...dragProps}>
              {imageList.length > 0 && (
                <div className={styles.attachImagesWrapper}>
                  {imageList.map((image, index) => (
                    <div key={index} className={styles.attachImage}>
                      <img src={image.dataURL} alt="" width="100" onClick={(): void => onImageUpdate(index)} />
                      <div className={styles.imageActions}>
                        <button onClick={(): void => onImageRemove(index)}>
                          <DeleteIcon />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className={styles.messageInputWrapper} ref={inputSuggestionRef} {...dragProps}>
                <AutocompleteSuggestion
                  suggestions={messagesRepeated}
                  onSelect={handleSelectMessageRepeated}
                  focus={focusInput}
                />
                <div className={styles.textInputWrapper}>
                  <TextareaAutosize
                    name="message"
                    placeholder="Message"
                    value={message.message}
                    maxRows={2}
                    onFocus={(): void => setFocusInput(true)}
                    onChange={(e): void => handleChange(e.target)}
                    onKeyDown={(event): void => {
                      if (event.key === "Enter" && event.shiftKey == true) {
                        event.preventDefault();
                        handleCreateMessage();
                      }
                    }}
                    ref={textareaRef}
                  />

                  <button onClick={onImageUpload} className={styles.attachButton}>
                    <AttachIcon />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </ImageUploading>
      <button onClick={(): void => handleCreateMessage()} className={styles.sendButton}>
        Send
      </button>
    </div>
  );
};

export default RightColumnFooterComponent;
